/**
 * SpeedyCore Identity SSO redirect helpers.
 * Central auth host: https://auth.speedycore.com.au
 */

const DEFAULT_AUTH_ORIGIN = "https://auth.speedycore.com.au";

function readEnv(name: string): string | undefined {
  try {
    const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
    if (proc?.env?.[name]) {
      return String(proc.env[name]);
    }
  } catch {
    /* ignore */
  }
  try {
    const meta = import.meta as { env?: Record<string, string | undefined> };
    if (meta.env?.[name]) return meta.env[name];
  } catch {
    /* ignore */
  }
  return undefined;
}

export function getAuthOrigin(): string {
  const raw =
    readEnv("SPEEDYCORE_AUTH_ORIGIN") ||
    readEnv("VITE_AUTH_ORIGIN") ||
    readEnv("VITE_IDENTITY_URL") ||
    DEFAULT_AUTH_ORIGIN;
  try {
    const u = new URL(raw);
    return `${u.protocol}//${u.host}`;
  } catch {
    return raw.replace(/\/$/, "");
  }
}

function currentUrlFallback(): string {
  if (typeof window !== "undefined" && window.location?.href) {
    return window.location.href;
  }
  return "https://speedycore.com.au/";
}

/**
 * Redirect browser to central login with return URL.
 * Pass `{ loggedOut: true }` after an explicit sign-out so the login page
 * skips the silent SSO handoff and does not immediately re-authenticate.
 * Pass `{ expired: true }` when the user's session expired to show the
 * friendly session-expired state copy.
 */
export function redirectToLogin(
  currentUrl?: string,
  options?: { loggedOut?: boolean; expired?: boolean },
): void {
  const redirect = encodeURIComponent(currentUrl || currentUrlFallback());
  let target = `${getAuthOrigin()}/auth/login?redirect_uri=${redirect}`;
  if (options?.loggedOut) target += "&logged_out=1";
  if (options?.expired) target += "&expired=1";
  if (typeof window !== "undefined") {
    window.location.assign(target);
  }
}

/** Redirect browser to central signup with return URL. */
export function redirectToSignup(currentUrl?: string): void {
  const redirect = encodeURIComponent(currentUrl || currentUrlFallback());
  const target = `${getAuthOrigin()}/auth/signup?redirect_uri=${redirect}`;
  if (typeof window !== "undefined") {
    window.location.assign(target);
  }
}

function safeRedirectUri(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    return u.toString();
  } catch {
    return null;
  }
}

/**
 * Handle /sso/callback after central login sets speedycore_session.
 * Resolves GET /identity/me with credentials, then bounces to redirect_uri.
 * For NON-speedycore redirect targets (constructiveos.com.au, creatorshub.com.au,
 * hubcircles.com.au, adverthub.com.au, pluginfactoryai.com, localhost/IDE), the
 * speedycore_session cookie is Domain=.speedycore.com.au and cannot reach those
 * hosts. Before redirecting, we mint Bearer tokens via identitySessionHandoff and
 * append them as a URL hash (#access_token=...&refresh_token=...&expires_in=...) so
 * the target SPA SaasHashConsumer can persist them to localStorage.
 */
export async function handleSSOCallback(options?: {
  identityBaseUrl?: string;
  defaultRedirect?: string;
  search?: string;
  handoffClientId?: "ide" | "identity_web";
}): Promise<{ ok: boolean; redirectTo?: string; error?: string }> {
  const params = new URLSearchParams(
    options?.search ?? (typeof window !== "undefined" ? window.location.search : ""),
  );
  const redirectUri =
    safeRedirectUri(params.get("redirect_uri")) ||
    options?.defaultRedirect ||
    (typeof window !== "undefined"
      ? `${window.location.protocol}//${window.location.host.replace(/^auth\./, "")}/`
      : "https://speedycore.com.au/");

  const identityBase = (options?.identityBaseUrl || "").replace(/\/$/, "");

  /** True when the speedycore_session cookie cannot reach the redirect host */
  function needsHashHandoff(target: string): boolean {
    try {
      const h = new URL(target).hostname.toLowerCase();
      if (h === "localhost" || h === "127.0.0.1") return true;
      if (h === "speedycore.com.au" || h.endsWith(".speedycore.com.au")) return false;
      return true;
    } catch {
      return true;
    }
  }

  try {
    const res = await fetch(`${identityBase}/identity/me`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) {
      redirectToLogin(redirectUri);
      return { ok: false, error: "SpeedyCore Identity: Session invalid", redirectTo: redirectUri };
    }

    let finalRedirect = redirectUri;
    if (typeof window !== "undefined" && needsHashHandoff(redirectUri)) {
      try {
        const clientId = options?.handoffClientId ?? "identity_web";
        const qs = clientId === "ide" ? "?client_id=ide" : "";
        const hand = await fetch(`${identityBase}/identity/session/handoff${qs}`, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        if (hand.ok) {
          const data = (await hand.json()) as {
            access_token?: string;
            refresh_token?: string;
            expires_in?: number;
            tenant_id?: string;
            user_id?: string;
            user?: { id?: string; email?: string; name?: string | null };
          };
          if (data?.access_token) {
            const u = new URL(redirectUri);
            const frag = new URLSearchParams();
            frag.set("access_token", data.access_token);
            if (typeof data.refresh_token === "string") frag.set("refresh_token", data.refresh_token);
            if (data.expires_in != null) frag.set("expires_in", String(data.expires_in));
            if (typeof data.tenant_id === "string" && data.tenant_id.trim()) {
              frag.set("tenant_id", data.tenant_id);
            }
            if (typeof data.user_id === "string" && data.user_id.trim()) {
              frag.set("user_id", data.user_id);
            }
            if (data.user) {
              if (typeof data.user.email === "string" && data.user.email.trim()) {
                frag.set("email", data.user.email);
              }
              if (typeof data.user.id === "string" && data.user.id.trim()) {
                frag.set("uid", data.user.id);
              }
              if (typeof data.user.name === "string" && data.user.name.trim()) {
                frag.set("name", data.user.name);
              }
            }
            u.hash = frag.toString();
            finalRedirect = u.toString();
          }
        }
      } catch {
        /* ignore handoff failure; fall back to plain redirect and hope for the best */
      }
    }

    if (typeof window !== "undefined") {
      window.location.replace(finalRedirect);
    }
    return { ok: true, redirectTo: finalRedirect };
  } catch {
    redirectToLogin(redirectUri);
    return { ok: false, error: "SpeedyCore Identity: Session invalid", redirectTo: redirectUri };
  }
}
